import { useEffect, useReducer, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Eye, EyeOff, Loader2, LogIn, UserPlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useAuth } from "@/context/AuthContext";

/**
 * Invite-acceptance landing page.
 *
 * Three distinct flows, all driven by the public `GET /invite-info` endpoint:
 *  1. **Brand-new invitee** (no Probe account yet) → inline sign-up form,
 *     creates the user as `active` (no super-admin gate) and drops them
 *     straight into the workspace.
 *  2. **Existing user, not signed in** → "Sign in to join" CTA that routes
 *     to /login?next=/accept-invite?token=… so we come back here after auth.
 *  3. **Existing user, signed in as the invitee** → auto-accept and land
 *     inside the workspace.
 */

const initial = {
  state: "loading",
  info: null,
  error: "",
  workspace: null,
};

function reducer(s, a) {
  switch (a.type) {
    case "info": return { ...s, state: "ready", info: a.info, error: "" };
    case "accepting": return { ...s, state: "accepting", error: "" };
    case "success": return { ...s, state: "success", workspace: a.workspace };
    case "error": return { ...s, state: "error", error: a.error };
    default: return s;
  }
}

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { refresh, switchTo } = useWorkspace();
  const [s, dispatch] = useReducer(reducer, initial);

  // Sign-up form state (only used when info.user_exists === false).
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const token = params.get("token");
  const isAuthed = !!(user && user.id);
  const inviteUrl = `/accept-invite?token=${token}`;

  // 1. Load invite metadata up-front (public endpoint).
  useEffect(() => {
    if (!token) {
      dispatch({ type: "error", error: "This invite link is missing a token." });
      return;
    }
    api.get(`/workspaces/invite-info?token=${encodeURIComponent(token)}`)
      .then(({ data }) => dispatch({ type: "info", info: data }))
      .catch((e) => dispatch({ type: "error", error: formatApiError(e) }));
  }, [token]);

  // 2. If invite is valid + invitee is already signed in as the right user,
  // auto-accept silently.
  useEffect(() => {
    if (s.state !== "ready" || !s.info) return;
    if (s.info.already_joined) return;
    if (!isAuthed) return;
    if (user.email !== s.info.invitee_email) return;
    // Same user — accept and land.
    (async () => {
      dispatch({ type: "accepting" });
      try {
        const { data } = await api.post("/workspaces/accept-invite", { token });
        await refresh();
        switchTo(data.id);
        dispatch({ type: "success", workspace: data });
      } catch (e) {
        dispatch({ type: "error", error: formatApiError(e) });
      }
    })();
  }, [s.state, s.info, isAuthed, user?.email, token, refresh, switchTo]);

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    dispatch({ type: "accepting" });
    try {
      const { data } = await api.post("/workspaces/accept-invite-signup", {
        token,
        name: name.trim(),
        password,
      });
      await refreshUser();
      await refresh();
      if (data.workspace?.id) {
        localStorage.setItem("probe_workspace_id", data.workspace.id);
        switchTo(data.workspace.id);
      }
      dispatch({ type: "success", workspace: data.workspace });
    } catch (e) {
      dispatch({ type: "error", error: formatApiError(e) });
    } finally {
      setSubmitting(false);
    }
  };

  // ----------- Render --------------------------------------------------

  if (s.state === "loading") {
    return (
      <Centered testId="accept-invite-loading">
        <div className="font-mono text-sm text-[#6B7280] flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={14} /> LOADING INVITE...
        </div>
      </Centered>
    );
  }

  if (s.state === "error") {
    return (
      <Centered testId="accept-invite-error">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#FEF2F2] text-[#B91C1C] mx-auto">
          <AlertTriangle size={20} />
        </div>
        <p className="font-mono text-[11px] tracking-[0.2em] text-[#6B7280] mt-4">INVITE PROBLEM</p>
        <h1 className="font-['Geist'] font-semibold text-2xl mt-1 text-[#0F172A]">{s.error}</h1>
        <p className="text-sm text-[#475569] mt-3">
          Ask the workspace owner to send you a new invite link.
        </p>
        <Link to="/" className="inline-block mt-6 h-10 px-5 text-xs font-semibold border border-[#D1D5DB] bg-white hover:bg-[#F9FAFB] inline-flex items-center">
          BACK TO PROBE
        </Link>
      </Centered>
    );
  }

  if (s.state === "success") {
    return (
      <Centered testId="accept-invite-success">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#ECFDF5] text-[#10B981] mx-auto">
          <CheckCircle2 size={20} />
        </div>
        <p className="font-mono text-[11px] tracking-[0.2em] text-[#6B7280] mt-4">JOINED WORKSPACE</p>
        <h1 className="font-['Geist'] font-semibold text-3xl mt-1 text-[#0F172A]">{s.workspace?.name}</h1>
        <p className="text-sm text-[#475569] mt-3">
          You&apos;re in as{" "}
          <span className="font-mono uppercase tracking-wider text-[#0F172A]">{s.workspace?.my_role}</span>.
        </p>
        <button
          onClick={() => navigate("/")}
          data-testid="invite-go-to-dashboard"
          className="mt-6 h-10 px-5 text-xs font-semibold bg-black text-white hover:bg-[#1F1F1F]"
        >
          GO TO WORKSPACE
        </button>
      </Centered>
    );
  }

  // s.state === "ready" or "accepting"
  const info = s.info;
  const isAccepting = s.state === "accepting";

  // Already-joined short-circuit.
  if (info?.already_joined) {
    return (
      <Centered testId="accept-invite-already-joined">
        <p className="font-mono text-[11px] tracking-[0.2em] text-[#6B7280]">INVITE ALREADY USED</p>
        <h1 className="font-['Geist'] font-semibold text-2xl mt-1 text-[#0F172A]">
          You&apos;ve already joined {info.workspace_name}
        </h1>
        <p className="text-sm text-[#475569] mt-3">
          {isAuthed
            ? "Head to your dashboard to pick up where you left off."
            : "Sign in to access the workspace."}
        </p>
        {isAuthed ? (
          <button
            onClick={() => navigate("/")}
            data-testid="already-joined-go-dashboard"
            className="mt-6 h-10 px-5 text-xs font-semibold bg-black text-white hover:bg-[#1F1F1F]"
          >
            GO TO DASHBOARD
          </button>
        ) : (
          <Link
            to="/login"
            data-testid="already-joined-sign-in"
            className="inline-block mt-6 h-10 px-5 text-xs font-semibold bg-black text-white hover:bg-[#1F1F1F] inline-flex items-center"
          >
            SIGN IN
          </Link>
        )}
      </Centered>
    );
  }

  // Authed as the WRONG user — block until they sign out.
  if (isAuthed && user.email !== info.invitee_email) {
    return (
      <Centered testId="accept-invite-wrong-user">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#FEF3C7] text-[#B45309] mx-auto">
          <AlertTriangle size={20} />
        </div>
        <p className="font-mono text-[11px] tracking-[0.2em] text-[#6B7280] mt-4">SIGNED IN AS A DIFFERENT ACCOUNT</p>
        <h1 className="font-['Geist'] font-semibold text-2xl mt-1 text-[#0F172A]">
          This invite was sent to {info.invitee_email}
        </h1>
        <p className="text-sm text-[#475569] mt-3">
          You&apos;re currently signed in as <span className="font-mono text-[#0F172A]">{user.email}</span>. Sign out and try the link again.
        </p>
      </Centered>
    );
  }

  // ============ The three primary CTAs =================================

  return (
    <div
      className="max-w-md mx-auto px-6 py-12"
      data-testid="accept-invite-page"
    >
      {/* Invite context header — same for both flows */}
      <div className="text-center mb-8">
        <p className="font-mono text-[11px] tracking-[0.22em] text-[#6B7280] uppercase">
          You&apos;ve been invited
        </p>
        <h1 className="font-['Geist'] font-semibold text-3xl mt-1 text-[#0F172A]" data-testid="invite-workspace-name">
          {info.workspace_name}
        </h1>
        <p className="text-sm text-[#475569] mt-3 leading-relaxed">
          {info.inviter_name ? (
            <>
              <span className="font-medium text-[#0F172A]">{info.inviter_name}</span> added you as{" "}
            </>
          ) : (
            "You'll join as "
          )}
          <span
            className="font-mono font-semibold text-[11px] uppercase tracking-wide text-[#2563EB] bg-[#EFF6FF] border border-[#BFDBFE] rounded px-1.5 py-[1px]"
            data-testid="invite-role-badge"
          >
            {info.role}
          </span>
          .
        </p>
      </div>

      {info.user_exists ? (
        /* === Existing user → Sign in to join === */
        <div
          className="border border-[#E5E7EB] rounded-2xl p-6 bg-white"
          data-testid="accept-invite-signin-card"
        >
          <p className="text-sm text-[#475569]">
            We found an existing Probe account for{" "}
            <span className="font-mono text-[#0F172A] bg-[#F1F5F9] px-1.5 py-0.5 rounded">
              {info.invitee_email}
            </span>
            . Sign in to accept this invite.
          </p>
          <Link
            to={`/login?next=${encodeURIComponent(inviteUrl)}`}
            data-testid="accept-invite-go-signin"
            className="mt-5 inline-flex items-center justify-center gap-2 h-10 w-full text-sm font-semibold rounded-md bg-black text-white hover:bg-[#1F1F1F]"
          >
            <LogIn size={14} /> Sign in to join
          </Link>
        </div>
      ) : (
        /* === Brand-new invitee → Inline sign-up === */
        <form
          onSubmit={handleSignupSubmit}
          className="border border-[#E5E7EB] rounded-2xl p-6 bg-white space-y-4"
          data-testid="accept-invite-signup-card"
        >
          <div>
            <label className="text-sm font-medium text-[#475569] block mb-1.5">Email</label>
            <Input
              value={info.invitee_email || ""}
              disabled
              data-testid="signup-email-locked"
              className="bg-[#F8FAFC] text-[#475569]"
            />
            <p className="text-xs text-[#94A3B8] mt-1">Locked to the invite address.</p>
          </div>
          <div>
            <label className="text-sm font-medium text-[#475569] block mb-1.5">Your name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              required
              autoFocus
              data-testid="signup-name-input"
              className="focus-visible:ring-[#3B82F6]"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-[#475569] block mb-1.5">Choose a password</label>
            <div className="relative">
              <Input
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                minLength={8}
                data-testid="signup-password-input"
                className="focus-visible:ring-[#3B82F6] pr-9"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#0F172A]"
                aria-label="Toggle password visibility"
              >
                {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <p className="text-xs text-[#94A3B8] mt-1">
              Must contain upper and lowercase letters, a number, and a symbol.
            </p>
          </div>
          {s.error && (
            <p className="text-sm text-[#B91C1C]" data-testid="signup-error">{s.error}</p>
          )}
          <button
            type="submit"
            disabled={submitting || isAccepting || !name.trim() || !password}
            data-testid="signup-submit"
            className="inline-flex items-center justify-center gap-2 h-10 w-full text-sm font-semibold rounded-md bg-black text-white hover:bg-[#1F1F1F] disabled:opacity-60"
          >
            {submitting || isAccepting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <UserPlus size={14} />
            )}
            Create account &amp; join
          </button>
          <p className="text-xs text-[#94A3B8] text-center pt-2 border-t border-[#F1F5F9]">
            Already have a Probe account?{" "}
            <Link
              to={`/login?next=${encodeURIComponent(inviteUrl)}`}
              className="text-[#3B82F6] hover:underline"
              data-testid="signup-switch-to-signin"
            >
              Sign in instead
            </Link>
          </p>
        </form>
      )}

      {isAccepting && (
        <p className="text-xs text-center text-[#64748B] mt-5 flex items-center justify-center gap-1.5">
          <Loader2 size={12} className="animate-spin" /> Setting up your workspace…
        </p>
      )}
    </div>
  );
}

function Centered({ children, testId }) {
  return (
    <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid={testId}>
      {children}
    </div>
  );
}
